import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { authClient, type User } from "@/lib/authClient";
import { generatePassword } from "@repo/lib/stringUtils";
import { toError } from "@repo/lib/utils";
import { Button } from "@repo/ui/components/Button";
import { Card, CardContent } from "@repo/ui/components/Card";
import { DataTable, type ColumnDef } from "@repo/ui/components/DataTable";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/DropdownMenu";
import { Input } from "@repo/ui/components/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui/components/Select";
import { useConfirmDialog } from "@repo/ui/hooks/useConfirmDialog";
import { toast } from "@repo/ui/lib/toast";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  KeyRound,
  Mail,
  MoreHorizontal,
  Search,
  Shield,
  ShieldAlert,
  Trash2,
  UserX,
} from "lucide-react";
import { useState } from "react";
import { InviteUserDialog } from "../../components/InviteUserDialog";

const itemsPerPage = 20;

export const Users = () => {
  const queryClient = useQueryClient();
  const confirm = useConfirmDialog();

  const [searchValue, setSearchValue, debouncedSearchValue] = useDebouncedValue("", 300);
  const [searchField, setSearchField] = useState<"email" | "name">("email");
  const [offset, setOffset] = useState(0);

  const { data, isLoading } = useQuery({
    queryKey: [
      "admin",
      "users",
      { debouncedSearchValue, searchField, limit: itemsPerPage, offset },
    ],
    queryFn: async () => {
      const response = await authClient.admin.listUsers({
        query: {
          searchValue: debouncedSearchValue || undefined,
          searchField: debouncedSearchValue ? searchField : undefined,
          limit: itemsPerPage,
          offset,
        },
      });

      if (!response.data) {
        throw new Error("Failed to fetch users");
      }

      // Cast to our inferred User type which includes requiresPasswordChange
      return {
        ...response.data,
        users: response.data.users as User[],
      };
    },
  });

  const { mutate: banUser } = useMutation({
    mutationFn: async (userId: string) => {
      const response = await authClient.admin.banUser({
        userId,
        banReason: "Banned by admin",
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to ban user");
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("User banned successfully");
    },
    onError: (err) => {
      toast.error(toError(err).message);
    },
  });

  const { mutate: unbanUser } = useMutation({
    mutationFn: async (userId: string) => {
      const response = await authClient.admin.unbanUser({
        userId,
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to unban user");
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("User unbanned successfully");
    },
    onError: (err) => {
      toast.error(toError(err).message);
    },
  });

  const { mutate: setRole } = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: "user" | "admin" }) => {
      const response = await authClient.admin.setRole({
        userId,
        role,
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to set user role");
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("User role updated successfully");
    },
    onError: (err) => {
      toast.error(toError(err).message);
    },
  });

  const { mutate: deleteUser } = useMutation({
    mutationFn: async (userId: string) => {
      const response = await authClient.admin.removeUser({
        userId,
      });

      if (response.error) {
        throw new Error(response.error.message || "Failed to delete user");
      }

      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });
      toast.success("User deleted successfully");
    },
    onError: (err) => {
      toast.error(toError(err).message);
    },
  });

  const handleDeleteUser = (user: User) => {
    confirm({
      title: "Delete User",
      message: (
        <>
          Are you sure you want to delete the user <strong>{user.name || user.email}</strong>? This
          action cannot be undone.
        </>
      ),
      confirmLabel: "Delete",
      closeLabel: "Cancel",
      color: "destructive",
      onConfirm: () => {
        deleteUser(user.id);
      },
    });
  };

  const { mutate: resetPassword } = useMutation({
    mutationFn: async (userId: string) => {
      const tempPassword = generatePassword();

      const passwordResponse = await authClient.admin.setUserPassword({
        userId,
        newPassword: tempPassword,
      });

      if (passwordResponse.error) {
        throw new Error(passwordResponse.error.message || "Failed to reset password");
      }

      const updateResponse = await authClient.admin.updateUser({
        userId,
        data: {
          requiresPasswordChange: true,
        },
      });

      if (updateResponse.error) {
        throw new Error(updateResponse.error.message || "Failed to set password change flag");
      }

      return { tempPassword };
    },
    onSuccess: ({ tempPassword }) => {
      void queryClient.invalidateQueries({ queryKey: ["admin", "users"] });

      confirm({
        title: "Password Reset Successful",
        message: (
          <div className="space-y-3">
            <p>A temporary password has been generated for this user.</p>
            <div className="bg-muted rounded-md p-3">
              <p className="mb-1 text-sm font-medium">Temporary Password:</p>
              <code className="text-primary font-mono text-lg font-semibold break-all">
                {tempPassword}
              </code>
            </div>
            <p className="text-muted-foreground text-sm">
              Click the button below to copy the password to your clipboard. The user will be
              required to change it on their next login.
            </p>
          </div>
        ),
        confirmLabel: "Copy Password",
        alert: true,
        onConfirm: async () => {
          await navigator.clipboard.writeText(tempPassword);
          toast.success("Password copied to clipboard!");
        },
      });
    },
    onError: (err) => {
      toast.error(toError(err).message);
    },
  });

  const handleResetPassword = (user: User) => {
    confirm({
      title: "Reset User Password",
      message: (
        <>
          Are you sure you want to reset the password for <strong>{user.name || user.email}</strong>
          ? A temporary password will be generated and the user will be required to change it on
          their next login.
        </>
      ),
      confirmLabel: "Reset Password",
      closeLabel: "Cancel",
      color: "destructive",
      onConfirm: () => {
        resetPassword(user.id);
      },
    });
  };

  const columns: ColumnDef<User>[] = [
    {
      id: "name",
      header: () => "Name",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.name}</span>
          {row.banned && (
            <span className="bg-destructive/10 text-destructive rounded-full px-2 py-0.5 text-xs">
              Banned
            </span>
          )}
        </div>
      ),
    },
    {
      id: "email",
      header: () => "Email",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">{row.email}</span>
          {row.emailVerified && <Mail className="text-muted-foreground h-3.5 w-3.5" />}
        </div>
      ),
    },
    {
      id: "role",
      header: () => "Role",
      cell: (row) => (
        <Select
          value={row.role ?? "user"}
          onValueChange={(value) => {
            setRole({ userId: row.id, role: value as "user" | "admin" });
          }}
        >
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">User</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
      ),
    },
    {
      id: "createdAt",
      header: () => "Created",
      cell: (row) => new Date(row.createdAt).toLocaleDateString(),
    },
  ];

  const users = data?.users ?? [];
  const total = data?.total ?? 0;
  const currentPage = Math.floor(offset / itemsPerPage) + 1;
  const totalPages = Math.ceil(total / itemsPerPage);

  return (
    <div className="flex flex-1 flex-col gap-6 p-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage users and roles</p>
        </div>

        <InviteUserDialog />
      </div>

      <Card>
        <CardContent>
          <div className="mb-4 flex gap-4">
            <div className="flex flex-1 gap-2">
              <div className="relative flex-1">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  placeholder="Search users..."
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value, () => {
                      setOffset(0);
                    });
                  }}
                  className="pl-9"
                />
              </div>
              <Select
                value={searchField}
                onValueChange={(value: "email" | "name") => setSearchField(value)}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="name">Name</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DataTable
            data={users}
            columns={columns}
            visibleColumns={columns.map((c) => c.id)}
            getRowId={(row) => row.id}
            loading={isLoading}
            actions={(row) => (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleResetPassword(row)}>
                    <KeyRound className="mr-2 h-4 w-4" />
                    Reset Password
                  </DropdownMenuItem>
                  {row.banned ? (
                    <DropdownMenuItem onClick={() => unbanUser(row.id)}>
                      <Shield className="mr-2 h-4 w-4" />
                      Unban User
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={() => banUser(row.id)}>
                      <ShieldAlert className="mr-2 h-4 w-4" />
                      Ban User
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={() => handleDeleteUser(row)}
                  >
                    <Trash2 className="text-destructive mr-2 h-4 w-4" />
                    Delete User
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            emptyState={
              <div className="flex min-h-[400px] flex-col items-center justify-center gap-2">
                <UserX className="text-muted-foreground h-12 w-12" />
                <p className="text-muted-foreground">No users found</p>
              </div>
            }
          />

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-muted-foreground text-sm">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - itemsPerPage))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(offset + itemsPerPage)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
